import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InlineSkeleton } from '@/components/core';
import { Badge, SKILL_CATEGORIES, SkillCategory } from '@/components/ui-kit/badge';
import { DescriptionListSkeleton } from '@/components/ui-kit/description-list';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';
import { useOrganizationNamesWithStatus } from '@/hooks/use-organizations';
import { formatDateOnly, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { workDaySettingsOf } from '@/lib/utils/work-days/work-days';
import {
  useGetEmployeeFineTotals,
  useGetEmployeeInsuranceIds,
} from '../../hooks/use-profile-view-lookups';
import { useGetSkillsFor, useGetSkillTaxonomy } from '../../hooks/use-skill-lookups';
import {
  useEmployeeWorkStatusEligibility,
  useIsWorkStatusEligibilityFetching,
} from '../../hooks/use-work-status-eligibility';
import { contactInfoFromEmployee } from '../../schemas/contact-info-schema';
import {
  experienceMonths,
  formatExperience,
  PROFICIENCY_LEVELS,
  resolveStoredSkill,
  resolveSubCategoryId,
  skillLookupOf,
  storedSkillName,
  toSkillValues,
  toSubCategoryExperienceValues,
} from '../../schemas/skills-schema';
import { isWorkingFromHome, sortOfficeDays } from '../../schemas/work-status';
import { Employee, HrTermRecord } from '../../types/employee.types';
import { Fact, FactList, ProfileSectionCard } from './profile-section-card';
import { hoursAndMinutes } from './profile-terms';

const SEX_LABELS: Record<string, string> = {
  MALE: 'Male',
  FEMALE: 'Female',
  OTHER: 'Other',
  NA: 'Not Specified',
};

const INSURANCE_ROWS = [
  { type: 'Employee', labelKey: 'USER_INSURANCE_ID' },
  { type: 'Spouse', labelKey: 'SPOUSE_INSURANCE_ID' },
  { type: 'Child1', labelKey: 'CHILD_1_INSURANCE_ID' },
  { type: 'Child2', labelKey: 'CHILD_2_INSURANCE_ID' },
  { type: 'Child3', labelKey: 'CHILD_3_INSURANCE_ID' },
] as const;

const formatAddress = (parts: (string | undefined)[]) => parts.filter(Boolean).join(', ');

interface CardProps {
  employee: Employee;
  /** The section's edit page; no pencil without it. */
  editTo?: string;
}

/** Private details show to the person themself, and to others only when the person chose to share them. */
export const showsPrivateInfo = (employee: Employee, isSelf: boolean) =>
  isSelf || employee.IsPrivateInfoVisible === true;

/** Employment: the organizations the person works in and their roles. */
export const EmploymentCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  // Each organization's name from its own IAM record, which every employee may read.
  const organizationIds = useMemo(
    () => [
      ...new Set(
        (employee.OrganizationAccess ?? []).map((organization) => organization.OrganizationId)
      ),
    ],
    [employee.OrganizationAccess]
  );
  const { names, isLoading } = useOrganizationNamesWithStatus(organizationIds);
  return (
    <ProfileSectionCard title={t('EMPLOYMENT')} editTo={editTo}>
      <FactList
        facts={[
          [
            t('ORGANIZATIONS'),
            isLoading ? (
              <InlineSkeleton className="w-40" />
            ) : (
              organizationIds.map((id) => names[id]).join(', ')
            ),
          ],
          [t('ROLE'), (employee.UserRoleKeys ?? []).join(', ')],
        ]}
      />
    </ProfileSectionCard>
  );
};

export const BasicInfoCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  return (
    <ProfileSectionCard title={t('BASIC_INFORMATION')} editTo={editTo}>
      <FactList
        facts={[
          [t('GENDER'), employee.Sex ? (SEX_LABELS[employee.Sex] ?? employee.Sex) : ''],
          [t('DATE_OF_BIRTH_CERTIFICATE'), formatDateOnly(employee.DateOfBirth)],
          [t('DATE_OF_BIRTH_ORIGINAL'), formatDateOnly(employee.DateOfBirthOriginal)],
        ]}
      />
    </ProfileSectionCard>
  );
};

/** The design-system skill colour for a sub-category title (SKL-01), if it is one of the known ones. */
export const skillCategoryOf = (title?: string): SkillCategory | undefined => {
  const name = (title ?? '').toLowerCase().replace(/[^a-z]/g, '');
  if (!name) return undefined;
  const keys = Object.keys(SKILL_CATEGORIES) as SkillCategory[];
  return (
    keys.find((key) => name === key) ??
    // Longest first, so "fullstack" isn't read as "ui" or "ux" hidden in a longer title.
    [...keys].sort((a, b) => b.length - a.length).find((key) => name.startsWith(key))
  );
};

/**
 * Expertise: the category, each sub-category with its period, and the skills as chips coloured by the
 * sub-category they belong to (SKL-01–03: tooltip with sub-category and level, and a colour key).
 */
export const ExpertiseCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  const { data: taxonomy, isLoading: isTaxonomyLoading } = useGetSkillTaxonomy();
  const { skillIds, subCategoryIds } = skillLookupOf(employee, taxonomy?.subCategories ?? []);
  const { data: skills = [], isLoading: isSkillsLoading } = useGetSkillsFor(
    skillIds,
    subCategoryIds,
    Boolean(taxonomy)
  );
  const subCategoryTitle = (id?: string) =>
    taxonomy?.subCategories.find((item) => item.ItemId === id)?.Title;

  const category = employee.CategoryId
    ? (taxonomy?.categories.find((item) => item.ItemId === employee.CategoryId)?.Title ??
      employee.CategoryId)
    : undefined;
  const experiences = toSubCategoryExperienceValues(employee.SubCategoryExperiences).map(
    (entry, index) => {
      const id = resolveSubCategoryId(entry.SubCategoryId, taxonomy?.subCategories ?? []);
      // The title saved with the entry covers a sub-category that no longer exists.
      const savedTitle = employee.SubCategoryExperiences?.[index]?.SubCategoryId
        ? employee.SubCategoryExperiences[index].SubCategory
        : undefined;
      return {
        ...entry,
        title: subCategoryTitle(id) || savedTitle || entry.SubCategoryId,
        duration: formatExperience(
          experienceMonths(entry.StartDate, entry.EndDate, entry.IsUntilNow)
        ),
      };
    }
  );
  // The skill's current name; one that no longer exists shows the name it was saved under.
  const ownSkills = toSkillValues(employee.Skills).map((entry) => {
    const skill = resolveStoredSkill(entry, skills, subCategoryIds);
    const subCategory = subCategoryTitle(skill?.SubCategoryId);
    return {
      name: skill?.Name ?? storedSkillName(entry),
      level: PROFICIENCY_LEVELS.find((level) => level.key === entry.Proficiency)?.title ?? '',
      subCategory,
      colour: skillCategoryOf(subCategory),
    };
  });
  const coloursShown = [
    ...new Set(ownSkills.map((skill) => skill.colour).filter((colour) => colour !== undefined)),
  ] as SkillCategory[];

  const facts: Fact[] = [
    ...(category ? [[t('CATEGORY'), category] as Fact] : []),
    ...experiences.map((entry): Fact => [
      entry.title,
      <span key={entry.title} className="tabular-nums">
        {formatDateOnly(toDateOnlyValue(entry.StartDate))} –{' '}
        {entry.IsUntilNow ? t('CURRENT') : formatDateOnly(toDateOnlyValue(entry.EndDate))}
        <span className="block text-xs font-normal text-muted-foreground">
          {entry.duration.years} {t('YEARS')} {entry.duration.months} {t('MONTHS')}
        </span>
      </span>,
    ]),
  ];

  return (
    <ProfileSectionCard title={t('EXPERTISE')} editTo={editTo}>
      {isTaxonomyLoading || isSkillsLoading ? (
        <DescriptionListSkeleton rows={3} />
      ) : (
        <div className="flex flex-col gap-3">
          <FactList facts={facts.length ? facts : [[t('SUB_CATEGORY'), '']]} />
          {ownSkills.length > 0 && (
            <TooltipProvider>
              <ul className="flex flex-wrap gap-1.5" aria-label={t('SKILLS')}>
                {ownSkills.map((skill) => (
                  <li key={skill.name}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          tabIndex={0}
                          variant={skill.colour ? 'skill' : 'outline'}
                          skill={skill.colour}
                        >
                          {skill.name}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        {[skill.subCategory, skill.level].filter(Boolean).join(' · ')}
                      </TooltipContent>
                    </Tooltip>
                  </li>
                ))}
              </ul>
              {/* SKL-03: colour is never the only cue. */}
              {coloursShown.length > 0 && (
                <ul
                  className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground"
                  aria-label={t('SKILL_COLOUR_KEY')}
                >
                  {coloursShown.map((colour) => (
                    <li key={colour} className="flex items-center gap-1.5">
                      <Badge variant="skill" skill={colour} className="size-2.5 p-0" />
                      {SKILL_CATEGORIES[colour]}
                    </li>
                  ))}
                </ul>
              )}
            </TooltipProvider>
          )}
        </div>
      )}
    </ProfileSectionCard>
  );
};

export const PrivateContactCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  return (
    <ProfileSectionCard title={t('PRIVATE_CONTACT')} editTo={editTo}>
      <FactList
        facts={[
          [t('EMAIL_PRIVATE'), employee.PrivateEmail],
          [t('PHONE_PRIVATE'), employee.PrivatePhoneNumber],
        ]}
      />
    </ProfileSectionCard>
  );
};

export const PersonalDetailsCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  return (
    <ProfileSectionCard title={t('PERSONAL_DETAILS')} editTo={editTo}>
      <FactList
        facts={[
          [t('BLOOD_GROUP'), employee.BloodGroup],
          [t('FATHERS_NAME'), employee.FathersName],
          [t('MOTHERS_NAME'), employee.MothersName],
          [t('SPOUSE_NAME'), employee.SpouseName],
          [t('HOBBIES_INTERESTS'), (employee.HobbiesInterests ?? []).join(', ')],
        ]}
      />
    </ProfileSectionCard>
  );
};

export const IdentityDocumentsCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  return (
    <ProfileSectionCard title={t('IDENTITY_DOCUMENTS')} editTo={editTo}>
      <FactList
        facts={[
          [t('NID'), employee.Nid],
          [t('E_TIN'), employee.Etin],
          [t('PASSPORT_NO'), employee.PassportNo],
          [t('PASSPORT_EXPIRY_DATE'), formatDateOnly(employee.PassportExpiryDate)],
        ]}
      />
    </ProfileSectionCard>
  );
};

export const EmergencyContactCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  return (
    <ProfileSectionCard title={t('EMERGENCY_CONTACT')} editTo={editTo}>
      <FactList
        facts={[
          [t('NAME'), employee.EmergencyContact?.Name],
          [t('RELATIONSHIP'), employee.EmergencyContact?.Relationship],
          [t('CONTACT_NO'), employee.EmergencyContact?.PhoneNumber],
        ]}
      />
    </ProfileSectionCard>
  );
};

export const AddressCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  const address = (value: Employee['PresentAddress']) =>
    formatAddress([value?.Address, value?.City, value?.Canton, value?.ZipCode, value?.Country]);
  return (
    <ProfileSectionCard title={t('ADDRESS')} editTo={editTo}>
      <FactList
        facts={[
          [t('PRESENT_ADDRESS'), address(employee.PresentAddress)],
          [t('PERMANENT_ADDRESS'), address(employee.PermanentAddress)],
        ]}
      />
    </ProfileSectionCard>
  );
};

export const EducationCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  return (
    <ProfileSectionCard title={t('EDUCATION_AND_PREVIOUS_WORK')} editTo={editTo}>
      <FactList
        facts={[
          [t('LATEST_ACADEMIC_TITLE'), employee.LatestAcademicTitle],
          [t('GRADUATION_INSTITUTE'), employee.GraduationInstitute],
          [t('POST_GRADUATION_INSTITUTE'), employee.PostGraduationInstitute],
          [
            t('PREVIOUS_EXPERIENCE'),
            employee.PreviousExperience ? `${employee.PreviousExperience} ${t('YEARS')}` : '',
          ],
          [t('PREVIOUS_ORGANIZATION'), employee.PreviousOrganization],
        ]}
      />
    </ProfileSectionCard>
  );
};

/** Links show as links only when they are http(s) URLs (legacy `isValidUrl`). */
const ExternalLink = ({ url }: { url?: string }) => {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2"
    >
      {url}
    </a>
  ) : (
    <span>{url}</span>
  );
};

export const SocialProfilesCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  const link = (url?: string) => (url ? <ExternalLink url={url} /> : '');
  return (
    <ProfileSectionCard title={t('SOCIAL_PROFILES')} editTo={editTo}>
      <FactList
        facts={[
          [t('LINKEDIN_PROFILE'), link(employee.LinkedInProfile)],
          [t('GITHUB_PROFILE'), link(employee.GithubProfile)],
          [t('FACEBOOK_PROFILE'), link(employee.FaceBookProfile)],
          [t('WHATSAPP_PROFILE'), link(employee.WhatsAppProfile)],
          [t('INSTAGRAM_PROFILE'), link(employee.InstagramProfile)],
          [t('PROFESSIONAL_IMAGE_URL'), link(employee.ProfessionalImageUrl)],
        ]}
      />
    </ProfileSectionCard>
  );
};

/** One card per reference the person gave; none when they gave none. */
export const ReferenceCards = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  const references = (contactInfoFromEmployee(employee).References ?? []).filter(
    (reference) => reference.Name || reference.Company || reference.Email || reference.Phone
  );
  return (
    <>
      {references.map((reference, index) => (
        <ProfileSectionCard
          key={`${reference.Name}-${index}`}
          title={references.length > 1 ? `${t('REFERENCE')} ${index + 1}` : t('REFERENCE')}
          editTo={editTo}
        >
          <FactList
            facts={[
              [t('NAME'), reference.Name],
              [t('DESIGNATION'), reference.Designation],
              [t('COMPANY'), reference.Company],
              [t('EMAIL'), reference.Email],
              [t('PHONE'), reference.Phone],
            ]}
          />
        </ProfileSectionCard>
      ))}
    </>
  );
};

export const InsuranceCard = ({ employee, editTo }: CardProps) => {
  const { t } = useTranslation();
  const { data: insurance = {}, isLoading } = useGetEmployeeInsuranceIds(employee.UserId);
  return (
    <ProfileSectionCard title={t('INSURANCE')} editTo={editTo}>
      <FactList
        facts={INSURANCE_ROWS.map((row): Fact => [
          t(row.labelKey),
          isLoading ? <InlineSkeleton className="w-24" /> : insurance[row.type],
        ])}
      />
    </ProfileSectionCard>
  );
};

interface TermCardProps extends CardProps {
  term: HrTermRecord;
}

export const HrPeriodCard = ({ term, editTo }: TermCardProps) => {
  const { t } = useTranslation();
  return (
    <ProfileSectionCard title={t('HR_PERIOD')} editTo={editTo}>
      <FactList
        facts={[
          [t('EMPLOYMENT_TYPE'), term.EmploymentType],
          [t('HR_PERIOD_START'), formatDateOnly(term.HrPeriodStartDate)],
          [t('HR_PERIOD_END'), formatDateOnly(term.HrPeriodEndDate)],
          [t('FLEXIBLE'), term.IsFlexible ? t('YES') : t('NO')],
        ]}
      />
    </ProfileSectionCard>
  );
};

/**
 * A term's working info: work days and hours per work-day setting, then (current term only, since they
 * describe the person now) work from home, office days, lunch and overtime.
 */
const useWorkingFacts = (employee: Employee, term: HrTermRecord): Fact[] => {
  const { t } = useTranslation();
  const isCurrent = term.IsCurrentHrYear;
  // Lunch / overtime are on when the employee holds PROFILE.LUNCH_ENABLED / OVERTIME_ENABLED.
  const eligibility = useEmployeeWorkStatusEligibility(isCurrent ? employee.UserId : undefined);
  const isEligibilityFetching = useIsWorkStatusEligibilityFetching(
    isCurrent ? employee.UserId : undefined
  );
  const statusText = (isOn: boolean | undefined) =>
    isOn === undefined ? (
      isEligibilityFetching ? (
        <InlineSkeleton className="w-16" />
      ) : undefined
    ) : isOn ? (
      t('ACTIVE')
    ) : (
      t('INACTIVE')
    );
  const isWfh = isWorkingFromHome(employee);

  // One pair of rows per work-day setting; a term whose work days change shows each from its first day.
  const settings = workDaySettingsOf(term.WorkDayHours);
  const workDayFacts: Fact[] = settings.length
    ? settings.flatMap(({ effectiveFrom, days }): Fact[] => {
        const totalMinutes = days.reduce((sum, day) => sum + Math.round(day.WorkingHours * 60), 0);
        const suffix =
          settings.length > 1
            ? ` (${effectiveFrom ? t('WORK_DAYS_FROM_DATE', { date: formatDateOnly(days[0].EffectiveFrom) }) : t('WORK_DAYS_FROM_TERM_START')})`
            : '';
        return [
          [`${t('WORKING_DAYS')}${suffix}`, days.map((entry) => t(entry.WorkDay)).join(', ')],
          [t('TOTAL_WORKING_HOURS'), hoursAndMinutes(totalMinutes)],
        ];
      })
    : [[t('WORKING_DAYS'), '']];

  return [
    ...workDayFacts,
    ...(isCurrent
      ? ([
          [t('WFH'), isWfh ? t('ACTIVE') : t('INACTIVE')],
          ...(isWfh
            ? []
            : [
                [
                  t('WORK_FROM_OFFICE'),
                  sortOfficeDays(employee.WFODays)
                    .map((day) => t(day))
                    .join(', '),
                ],
              ]),
          [t('LUNCH_STATUS'), statusText(eligibility?.lunch)],
          [t('OVERTIME'), statusText(eligibility?.overtime)],
        ] as Fact[])
      : []),
  ];
};

/** A term's fines; not "0" while the totals load, which would read as "no fines". */
const useFineFacts = (employee: Employee, term: HrTermRecord): Fact[] => {
  const { t } = useTranslation();
  const { data: fines, isLoading } = useGetEmployeeFineTotals(
    employee.UserId,
    term.HrPeriodStartDate,
    term.HrPeriodEndDate
  );
  const amount = (value?: number) =>
    isLoading ? (
      <InlineSkeleton className="w-12" />
    ) : (
      <span className="tabular-nums">{`${value ?? 0}`}</span>
    );
  return [
    [t('LUNCH_FINE'), amount(fines?.lunch)],
    [t('NON_COMPLIANCE_FINE'), amount(fines?.nonCompliance)],
  ];
};

export const WorkingInfoCard = ({ employee, term, editTo }: TermCardProps) => {
  const { t } = useTranslation();
  return (
    <ProfileSectionCard title={t('WORKING_INFO')} editTo={editTo}>
      <FactList facts={useWorkingFacts(employee, term)} />
    </ProfileSectionCard>
  );
};

export const FinesCard = ({ employee, term, editTo }: TermCardProps) => {
  const { t } = useTranslation();
  return (
    <ProfileSectionCard title={t('FINES')} editTo={editTo}>
      <FactList facts={useFineFacts(employee, term)} />
    </ProfileSectionCard>
  );
};

/** Employee details › Attendance & Overtime: the selected term's working info and fines in one card. */
export const AttendanceOvertimeCard = ({ employee, term, editTo }: TermCardProps) => {
  const { t } = useTranslation();
  const facts = [...useWorkingFacts(employee, term), ...useFineFacts(employee, term)];
  return (
    <ProfileSectionCard
      title={t('ATTENDANCE_AND_OVERTIME')}
      description={t('HR_TERM_RANGE', {
        start: formatDateOnly(term.HrPeriodStartDate),
        end: formatDateOnly(term.HrPeriodEndDate),
      })}
      editTo={editTo}
    >
      <FactList facts={facts} />
    </ProfileSectionCard>
  );
};

/** A term's leave allotment: each applicable leave, then the carry-forwards. */
export const LeaveAllotmentCard = ({ term, editTo, title }: TermCardProps & { title?: string }) => {
  const { t } = useTranslation();
  const leaves = term.LeaveDetails.filter((leave) => leave.IsApplicable);
  const days = (value: number) => (
    <span className="tabular-nums">
      {value} {t('DAYS')}
    </span>
  );
  return (
    <ProfileSectionCard
      title={title ?? t('LEAVE_ALLOTMENT')}
      description={t('HR_TERM_RANGE', {
        start: formatDateOnly(term.HrPeriodStartDate),
        end: formatDateOnly(term.HrPeriodEndDate),
      })}
      editTo={editTo}
    >
      <FactList
        facts={[
          ...(leaves.length ? [] : [[t('LEAVE_ALLOTMENT'), ''] as Fact]),
          ...leaves.map((leave): Fact => [
            leave.IsDefault
              ? `${leave.LeaveCategory} (${t('DEFAULT_LEAVE')})`
              : leave.LeaveCategory,
            leave.IsUnlimited ? t('UNLIMITED') : days(leave.LeaveLimitTotalDays),
          ]),
          [t('LEAVE_CARRY_FORWARD'), days(term.LeaveCarryForwardDays)],
          [t('TOIL_CARRY_FORWARD'), days(term.ToilCarryForwardDays)],
          [t('SPECIAL_TOIL_CARRY_FORWARD'), days(term.SpecialToilCarryForwardDays)],
        ]}
      />
    </ProfileSectionCard>
  );
};

/** KPIs wait for the KPI List (Settings › KPIs, Soon): an empty state until then. */
export const KpisCard = () => {
  const { t } = useTranslation();
  return (
    <ProfileSectionCard title={t('KPIS')} description={t('KPIS_SCORED_BY_MANAGER')}>
      <p className="py-2 text-sm text-muted-foreground">{t('NO_KPIS_ASSIGNED_YET')}</p>
    </ProfileSectionCard>
  );
};
